import { Model, View } from '@lernetz/ts-lib';
import { Subscription } from '@lernetz/ts-lib/src/Signal';
import { context } from '@lernetz/ts-lib/src/view/Template';
import type { Asset } from '../Asset';

import template from './Cartoon.html';

export class Cartoon extends Model {

    public readonly id: string;

    public readonly alt?: string;
    public readonly backgroundImage: Asset;
    public readonly panels: CartoonPanel[];
    public readonly poster: Asset;
    public readonly width: string;
    public readonly marginTop: string;
    public readonly marginBottom: string;

    public canNext: boolean;
    public canPrevious: boolean;
    public currentPanel: CartoonPanel;
    public started: boolean;

    public allowHorizontalOverflow: boolean;
    
    public constructor(dto) {
        super({
            allowHorizontalOverflow: false,
            canNext: false,
            canPrevious: false,
            currentPanel: null,
            marginTop: '',
            marginBottom: '',
            panels: [],
            started: false,
            ...dto,
        });

        if( !this.panels.length ) return;

        this.propertyChange.filter( p => p.name === 'currentPanel' ).subscribe( () => {
            const index = this.getCurrentPanelIndex();
            this.canNext = index < this.panels.length - 1;
            this.canPrevious = index > 0;
        } );
    }

    public next() {
        if( !this.canNext ) return;
        this.currentPanel = this.panels[ this.getCurrentPanelIndex() + 1 ];
    }

    public previous() {
        if( !this.canPrevious ) return;
        this.currentPanel = this.panels[ this.getCurrentPanelIndex() - 1 ];
    }

    public start() {
        this.started = true;
        this.currentPanel = this.panels[ 0 ];
    }

    private getCurrentPanelIndex() {
        return this.panels.indexOf( this.currentPanel );
    }
}

export class CartoonPanel extends Model {

    public readonly alt?: string;
    public readonly height: number;
    public readonly left: number;
    public readonly order: number;
    public readonly overlayImage: Asset;
    public readonly top: number;
    public readonly width: number;
}

export class CartoonView extends View<Cartoon> {

    private currentPanelSubscription: Subscription;

    public constructor() {
        super({ template });
        this.registry.add('Image', data => new class extends View<Asset> {
            init() {
                if( !this.data ) return;
                const node = <HTMLImageElement> this.node;
                node.src = context.imagePath(this.data, 'large');
            }
        }({ template: '' }));
    }

    public init() {
        this.currentPanelSubscription = this.data.propertyChange.filter( p => p.name === 'currentPanel' ).subscribe( this.onCurrentPanelChanged.bind( this ) );
    }

    public deinit() {
        this.currentPanelSubscription.unsubscribe();
    }

    protected onCurrentPanelChanged() {
        const { top, left, width, height } = this.data.currentPanel; 
        const contentNode = this.node.querySelector<HTMLElement>( '.content' );
        contentNode.style.transformOrigin = `top left`;
        contentNode.style.transform = `scale(${ 1 / ( ( width + height ) / 2) }) translateY(${ -top * 100 }%) translateX(${ -left * 100 }%)`;
    }
}
